import { observer } from 'mobx-react-lite'
import React, { useContext, useMemo, useEffect, useState } from 'react'
import { CameraVideo, Heart, Image, PeopleFill, PersonPlusFill, PersonVcard } from 'react-bootstrap-icons'
import { useParams, useNavigate } from 'react-router-dom'
import { InfluencerContext } from '../../../../../contexts/InfluencerContext'
import { SharedCampaignContext } from '../../../../../contexts/Advertiser/SharedCampaignContext'
import { SharedInfluencerContext } from '../../../../../contexts/Advertiser/SharedInfluencerContext'
import Authentication from '../../../../../models/Authentication'
import { formatNumber } from '../../../../../utils/Helper'
import AnalyticsBox from './AnalyticsBox'
import axiosService from '../../../../../utils/AxiosService'
import { toastError } from '../../../../../components/FormComponents/Toasts'

export default observer(function Analytics() {
  const params = useParams()
  const { id, authentication_id, campaign_authentication_id } = params
  const navigate = useNavigate()
  const influencerContext = useContext(InfluencerContext)
  const sharedCampaignContext = useContext(SharedCampaignContext)
  const sharedInfluencerContext = useContext(SharedInfluencerContext)
  const [authentications, setAuthentications] = useState([])

  const isAdvertiserContext = !!sharedCampaignContext?.campaignState
  const isInfluencerShareContext = !!sharedInfluencerContext?.influencerState
  const currentId = isInfluencerShareContext
    ? (sharedInfluencerContext?.authentications?.[0]?.id)
    : (isAdvertiserContext ? authentication_id : id)

  useEffect(() => {
    if (isAdvertiserContext && authentication_id) {
      axiosService.get(`/api/advertiser/campaign_share/authentications/${authentication_id}/authentications`)
        .then(authentications => {
          const mappedAuthentications = authentications.map(authentication => new Authentication(authentication))
          setAuthentications(mappedAuthentications)
        })
        .catch((error) => {
          const errorMessage = error?.response?.data?.error || error?.data?.error || error?.error || error?.message || 'An error occurred'
          toastError(errorMessage)
        })
    }
  }, [isAdvertiserContext, authentication_id])

  const pmAuthentications = influencerContext?.authentications || []
  const sharedInfluencerAuthentications = sharedInfluencerContext?.authentications || []
  const currentAuthentications = isInfluencerShareContext
    ? sharedInfluencerAuthentications
    : (isAdvertiserContext ? authentications : pmAuthentications)

  const currentAuthentication = useMemo(() => currentAuthentications.find(authentication => +authentication.id === +currentId ) || new Authentication(), [currentId, currentAuthentications, currentAuthentications.length] )

  const realViewRate = useMemo(() => {
    if(!currentAuthentication.authentication_histories || currentAuthentication.authentication_histories.length < 1) return '0.00'
    const latestHistory = currentAuthentication.authentication_histories[currentAuthentication.authentication_histories.length - 1]
    const avgReelsPlays = latestHistory.avg_reels_plays
    const followerCount = latestHistory.follower_count

    if(!avgReelsPlays || !followerCount || followerCount === 0) return '0.00'

    return (avgReelsPlays / followerCount * 100).toFixed(2)
  }, [currentAuthentication.id, currentAuthentication.authentication_histories?.length])

  const shortFormViewRateLabel = currentAuthentication.provider === 'youtube' ? 'Short view rate' : 'Reel view rate'

  const postingsPerWeek = useMemo(() => {
    if(!currentAuthentication.authentication_histories || currentAuthentication.authentication_histories.length < 1) return null
    const latestHistory = currentAuthentication.authentication_histories[currentAuthentication.authentication_histories.length - 1]
    if(!latestHistory.avg_posts_4_weeks) return null

    return (latestHistory.avg_posts_4_weeks / 4).toFixed(1)
  }, [currentAuthentication.id, currentAuthentication.authentication_histories?.length])

  const followerGrowth = useMemo(() => {
    if(!currentAuthentication.authentication_histories || currentAuthentication.authentication_histories.length < 2) return ''
    const currentFollowerCount = currentAuthentication.authentication_histories[currentAuthentication.authentication_histories.length - 1].follower_count
    const previousFollowerCount = currentAuthentication.authentication_histories[currentAuthentication.authentication_histories.length - 2].follower_count
    return ((currentFollowerCount - previousFollowerCount) / previousFollowerCount * 100).toFixed(2)
  }, [currentAuthentication.id, currentAuthentication.authentication_histories?.length])


  return (
    <div className="w-full bg-white dark:bg-rb-dark-mode p-4 rounded-md">
      <div className="flex justify-between">
        <p className="font-bold text-xl mb-4 dark:text-white">Analytics</p>
        {!isAdvertiserContext && !isInfluencerShareContext && <button className="font-bold text-sm dark:text-white" onClick={() => navigate(`/project_manager/authentications/${currentAuthentication.id}/influencer/statistics`)}>Go to statistics</button>}
      </div>
      <div className="grid grid-cols-3 gap-4">
        <AnalyticsBox text="Followers" icon={<PeopleFill/>} value={formatNumber(currentAuthentication.reach)} />
        <AnalyticsBox text="Follower growth" icon={<PersonPlusFill />}  value={`${followerGrowth}%`}/>
        <AnalyticsBox text="Engagement rate" icon={<Heart />} value={`${(+currentAuthentication.engagement_rate * 100).toFixed(2)}%`} />
        <AnalyticsBox text="Postings per week" icon={<Image />} value={postingsPerWeek}/>
        <AnalyticsBox text={shortFormViewRateLabel} icon={<CameraVideo />} value={`${realViewRate}%`}/>
        <AnalyticsBox text="Audience Age Group" icon={<PersonVcard />} value={currentAuthentication.age_group} />
      </div>
    </div>
  )
})